import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowUpRight, MapPin } from "lucide-react";

import { fetchEditions } from "../api/issues";
import { Spinner } from "../components/Spinner";
import { fmtIST, shortDate } from "../lib/format";

function fmtMB(b: number | null): string {
  if (b === null) return "—";
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export function Publisher() {
  const params = useParams<{ channelId: string }>();
  const channelId = Number(params.channelId);
  const q = useQuery({
    queryKey: ["editions", channelId],
    queryFn: () => fetchEditions(channelId),
    enabled: !!channelId,
  });

  const channelName = q.data?.[0]?.channel_name ?? `Channel ${channelId}`;

  return (
    <div className="flex flex-col gap-8 animate-fade-up">
      <Link to="/browse"
            className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-muted)] hover:text-[var(--fg)] inline-flex items-center gap-1.5">
        <ArrowLeft size={12} /> All publishers
      </Link>

      <header className="rule-double pt-6">
        <div className="np-label mb-2">Section II · Editions</div>
        <h1 className="font-display font-extrabold tracking-tight leading-[0.94]"
            style={{ fontSize: "clamp(40px, 5vw, 64px)" }}>
          {channelName}
        </h1>
        {q.data && (
          <p className="font-display italic text-[18px] text-[var(--fg-muted)] mt-3">
            {q.data.filter(e => e.is_crawlable).length} cities crawled out of {q.data.length}.
            Pick a city to see its issues.
          </p>
        )}
      </header>

      {q.isLoading && <div className="py-12 flex justify-center"><Spinner label="Loading editions" /></div>}

      {q.isError && (
        <div className="np-banner">
          <span className="font-mono text-[10px] uppercase tracking-folio mr-2 text-[var(--accent)]">Error</span>
          Failed to load editions.
        </div>
      )}

      {q.data && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {q.data.map((e) => (
            <Link
              key={e.edition_id}
              to={`/browse/${channelId}/${e.edition_id}`}
              className="np-card p-5 group hover:shadow-plate transition-shadow flex flex-col gap-3"
              style={!e.is_crawlable ? { opacity: 0.55 } : undefined}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-[var(--fg-faint)] mb-0.5">
                    <MapPin size={11} />
                    <span className="font-mono text-[10px] uppercase tracking-folio">
                      Ed · {e.edition_id}
                    </span>
                    {e.is_crawlable ? (
                      <span className="np-chip np-chip--admin ml-1">live</span>
                    ) : (
                      <span className="np-chip">paused</span>
                    )}
                  </div>
                  <h3 className="font-display text-[22px] leading-tight font-medium truncate">
                    {e.city || e.edition_name || "—"}
                  </h3>
                  <div className="font-mono text-[11px] text-[var(--fg-muted)] mt-0.5 truncate">
                    {e.state ?? "—"}
                    {e.language ? ` · ${e.language}` : ""}
                  </div>
                </div>
                <ArrowUpRight size={15} className="text-[var(--fg-faint)] shrink-0 transition-colors group-hover:text-[var(--accent)]" />
              </div>

              <div className="np-divider" />

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <div className="np-label">Last issue</div>
                  <div className="font-mono text-[11px] text-[var(--fg)] mt-0.5">
                    {e.last_issue_date ? shortDate(e.last_issue_date) : "—"}
                    {fmtIST(e.last_issue_fetched_at ?? null) && (
                      <span className="text-[var(--fg-muted)]">
                        {" · "}{fmtIST(e.last_issue_fetched_at ?? null)}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="np-label">Pages</div>
                  <div className="font-display text-[18px] tabular-nums leading-none mt-0.5">
                    {e.last_issue_pages ?? "—"}
                  </div>
                </div>
                <div>
                  <div className="np-label">Size</div>
                  <div className="font-mono text-[12px] tabular-nums mt-0.5">
                    {fmtMB(e.last_issue_bytes)}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
